import { useContext, useRef, useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

import { Paper, Grid, Box, Typography, FormControlLabel, Switch, Button, 
    Accordion, AccordionSummary, AccordionDetails, 
    TextField} from '@mui/material';
import { CurrProfileContext } from '../App/App.tsx';
import { ExpandMore } from '@mui/icons-material';

export default function Settings() {
    const minUsernameLength = 4;
    const maxUsernameLength = 30;

    const initialized = useRef(false);
    const { profile, setProfile } = useContext(CurrProfileContext);

    const [upcomingPublic, setUpcomingPublic] = useState(false);
    const [pastPublic, setPastPublic] = useState(false);
    const [newUsername, setUsername] = useState("");

    async function setEventPrivacy() {
        if(!profile) {
            alert("You are not logged in.");
            return false;
        }
        await supabase.from("profiles")
            .update({ public_future : upcomingPublic, public_past : pastPublic })
            .eq("id", profile.id);
        return true;
    }

    // returns error message if invalid, null otherwise
    function validateUsername() {
        if(newUsername === '') {
            return null;
        } else if(newUsername.length < minUsernameLength) {
            return "Must be at least " + minUsernameLength + " characters.";
        } else if (newUsername.length > maxUsernameLength) {
            return "Cannot be greater than " + maxUsernameLength + " characters."
        } else if(newUsername.replaceAll(/[^0-9a-z._]/g, '') !== newUsername) {
            return "Invalid characters.";
        } else {
            return null; // validated
        }
    }

    async function setNewUsername() {
        if(!profile) { 
            alert("You are not logged in.");
            return false;
        }
        const { count, error } = await supabase
            .from("profiles")
            .select("*", { head: true, count: 'exact' })
            .eq("username", newUsername);
        if(error) {
            console.error("Failed to search for username: " + error.message);
            return false;
        } else if(count && count > 0) {
            alert("That username is already taken.");
            return false;
        } else {
            await supabase.from("profiles")
                .update({ username : newUsername })
                .eq("id", profile.id);
            return true;
        }
    }

    async function logout() {
        if(window.confirm("Are you sure you want to sign out?")) {
            supabase.auth.signOut().then((res) => {
                if(res.error) {
                console.error("Failed to sign out: " + res.error.message);
                } else {
                console.log("Logged out.");
        
                // any cached data from previous session should be destroyed
                setProfile(null);
                window.location.reload();
                }
            });
        }
    }

    async function deleteAccount() {
        const res = window.prompt("Are you sure you want to delete your account? This action CANNOT BE UNDONE. Enter 'delete' into the space below to finalize this action.");
        if(res === "delete") {
            await supabase.rpc("delete_user");
            await supabase.auth.signOut();
            setProfile(null);
            window.location.reload();
        }
    }

    useEffect(()=> {
        if(!initialized.current && profile) {
            setUpcomingPublic(profile?.public_future);
            setPastPublic(profile?.public_past);
            initialized.current = true
        }
    }, []);



    return <div style={{
        flex: '1', background: 'gray', flexDirection: 'column',
        display: 'flex', justifyContent: 'start',  padding: '2vh 4vw'
    }}>
        <Grid container sx={{ flex: '1', width: '100%', margin: 'auto' }} >
            <Paper elevation={10} sx={{ width: '100%', backgroundColor: 'lightcyan' }} >
                <Box sx={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                    gap: '14px', margin: { xs: '2.5vh 8vw', md: '4vh 14vw' }
                }}>
                    <Typography variant="h4" fontWeight='bold' sx={{ mb: 1 }}>
                        Settings
                    </Typography>

                    <Accordion sx={{ width: '100%' }} defaultExpanded>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                            <Typography component="span"><b>Event Privacy</b></Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: '14px', }}>
                            <FormControlLabel control={
                                <Switch checked={pastPublic}
                                    onChange={(e) => setPastPublic(e.target.checked)}
                                    slotProps={{ input: { 'aria-label': 'controlled' } }}
                                />
                            } label="My past events are public"/>
                            <FormControlLabel control={
                                <Switch checked={upcomingPublic}
                                    onChange={(e) => setUpcomingPublic(e.target.checked)}
                                    slotProps={{ input: { 'aria-label': 'controlled' } }}
                                />
                            } label="My upcoming events are public"/>
                            <Button disabled={
                                profile?.public_future === upcomingPublic && profile.public_past === pastPublic
                            }
                                onClick={() => {
                                    setEventPrivacy().then((success) => {
                                        if(success && profile) {
                                            setProfile({ ...profile, 
                                                public_future: upcomingPublic, 
                                                public_past: pastPublic
                                            });
                                        }
                                    });
                                }} 
                            variant='text'>Save Privacy Settings</Button>
                        </AccordionDetails>
                    </Accordion>

                    <Accordion sx={{ width: '100%' }}>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                            <Typography component="span"><b>Change Username</b></Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                            <Typography textAlign='center'>You can change your account handle to any unique username not linked to an existing account. Usernames must be 4-30 characters and can only contain numbers, lowercase letters, periods, and underscorres.</Typography>
                            <TextField label="New Username" value={newUsername}
                                error={validateUsername() !== null}
                                helperText={validateUsername()}
                                onChange={(e) => { setUsername(e.target.value)}}
                            />
                            <Button disabled={newUsername === '' || validateUsername() !== null }
                                onClick={() => {
                                    setNewUsername().then((success) => {
                                        if(success && profile) {
                                            setProfile({ ...profile, username: newUsername });
                                            setUsername("");
                                        }
                                    })
                                }} variant='text'
                            >Set New Username</Button>
                        </AccordionDetails>
                    </Accordion>

                    <Typography variant='body2' sx={{ textAlign: 'left', width: '100%' }}
                        ><b>UID:</b> {profile?.id}</Typography>
                    <Button onClick={() => { logout(); }} variant='contained'
                        >Sign out</Button>
                    <Button onClick={() => { deleteAccount(); }} variant='contained' color='error'
                        >Delete Account</Button>
                </Box>
            </Paper>
        </Grid>
    </div>;
}